import {
  Box,
  Typography,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const ensureList = (value) =>
  value && value.length > 0 ? value : [{ key: '', value: '' }];

// Nuevo: listas opcionales (permite 0 filas)
const ensureOptionalList = (value) => (Array.isArray(value) ? value : []);

const ParamList = ({ title, items, onChange, onAdd, onRemove, onClear, keyLabel, valueLabel, valuePlaceholder }) => (
  <Box sx={{ mt: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton onClick={onAdd} size="small" aria-label={`Agregar ${title}`}>
          <AddCircleOutlineIcon fontSize="small" />
          <Typography variant="caption" sx={{ ml: 0.5 }}>
            Agregar
          </Typography>
        </IconButton>
        <IconButton onClick={onClear} size="small" aria-label={`Limpiar ${title}`}>
          <DeleteIcon fontSize="small" />
          <Typography variant="caption" sx={{ ml: 0.5 }}>
            Limpiar
          </Typography>
        </IconButton>
      </Box>
    </Box>

    {items.length === 0 ? (
      <Typography variant="caption" color="text.secondary">
        Sin {title.toLowerCase()}.
      </Typography>
    ) : (
      items.map((param, index) => (
        <Box
          key={`${param.key}-${index}`}
          sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
        >
          <TextField
            label={keyLabel}
            value={param.key || ''}
            size="small"
            fullWidth
            onChange={(e) => onChange(index, 'key', e.target.value)}
          />
          <TextField
            label={valueLabel}
            value={param.value || ''}
            size="small"
            fullWidth
            placeholder={valuePlaceholder}
            onChange={(e) => onChange(index, 'value', e.target.value)}
          />
          <IconButton
            size="small"
            onClick={() => onRemove(index)}
            aria-label="Eliminar"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))
    )}
  </Box>
);

export function APICallNodeFields({ localNodeData, updateNodeData }) {
  // Antes se forzaba al menos 1 fila vacía; ahora permitimos 0 filas.
  const queryParams = ensureOptionalList(localNodeData.queryParams);
  const headers = ensureOptionalList(localNodeData.headers);

  // Compatibilidad: en algún momento se intentó guardar como `body`.
  // Usamos `APIBody` como clave principal (coincide con defaults/otros nodos).
  const apiBodyValue = localNodeData.APIBody ?? localNodeData.body ?? '';

  const handleListChange = (listKey, index, field, value) => {
    const list = listKey === 'queryParams' ? queryParams : headers;
    const updated = list.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    updateNodeData(listKey, updated);
  };

  const handleAdd = (listKey) => {
    const list = listKey === 'queryParams' ? queryParams : headers;
    updateNodeData(listKey, [...list, { key: '', value: '' }]);
  };

  const handleClear = (listKey) => {
    updateNodeData(listKey, []);
  };

  const handleRemove = (listKey, index) => {
    const list = listKey === 'queryParams' ? queryParams : headers;
    updateNodeData(
      listKey,
      list.filter((_, i) => i !== index)
    );
  };

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          URL de la API
        </Typography>
        <TextField
          value={localNodeData.url || ''}
          size="small"
          fullWidth
          placeholder="https://api.midominio.cl/clientes"
          onChange={(e) => updateNodeData('url', e.target.value)}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="method-select-label">Metodo</InputLabel>
          <Select
            labelId="method-select-label"
            value={localNodeData.method || 'GET'}
            label="Metodo"
            onChange={(e) => updateNodeData('method', e.target.value)}
          >
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <ParamList
        title="Query params"
        items={queryParams}
        onChange={(index, field, value) => handleListChange('queryParams', index, field, value)}
        onAdd={() => handleAdd('queryParams')}
        onRemove={(index) => handleRemove('queryParams', index)}
        onClear={() => handleClear('queryParams')}
        keyLabel="Llave"
        valueLabel="Valor"
        valuePlaceholder="Ej: {{rut_cliente}}"
      />

      <ParamList
        title="Headers"
        items={headers}
        onChange={(index, field, value) => handleListChange('headers', index, field, value)}
        onAdd={() => handleAdd('headers')}
        onRemove={(index) => handleRemove('headers', index)}
        onClear={() => handleClear('headers')}
        keyLabel="Header"
        valueLabel="Valor"
        valuePlaceholder="Ej: Bearer {{token}}"
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Body (para POST/PUT)
        </Typography>
        <TextField
          value={apiBodyValue}
          size="small"
          fullWidth
          multiline
          rows={4}
          placeholder='{ "rut": "{{rut_cliente}}" }'
          onChange={(e) => updateNodeData('APIBody', e.target.value)}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Guardar resultado como
        </Typography>
        <TextField
          value={localNodeData.OutputKey || ''}
          size="small"
          fullWidth
          placeholder="Ej: cliente"
          onChange={(e) => updateNodeData('OutputKey', e.target.value)}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localNodeData.expectMultiple || false}
              onChange={(e) => updateNodeData('expectMultiple', e.target.checked)}
            />
          }
          label="La llamada puede devolver varios registros"
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localNodeData.noWaitResponse || false}
              onChange={(e) => updateNodeData('noWaitResponse', e.target.checked)}
            />
          }
          label="No esperar respuesta (fire-and-forget)"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Si está activado, el flujo no debería depender del resultado de esta llamada.
        </Typography>
      </Box>
    </>
  );
}
